<script setup lang="ts">
/**
 * We use the VueDatepicker component from https://vue3datepicker.com/ to handle date inputs.
 */
import VueDatePicker from '@vuepic/vue-datepicker'
import { format, parse } from 'date-fns'

import '@vuepic/vue-datepicker/dist/main.css'

import LabelInput from '~/components/ui/LabelInput.vue'

import { useLanguage } from '~/composables/useLanguage'

import { hasSlotContent } from '~/utils/hasSlot'

type Props = {
  modelValue: string | null | undefined
  label: string
  labelAnnotation?: string
  isOptionalField?: boolean
  outputFormat?: string
  validity?: boolean
  isTextInput?: boolean
}

type Emits = {
  'update:modelValue': [payload: string | null]
}

const props = withDefaults(defineProps<Props>(), {
  isOptionalField: true,
  outputFormat: 'yyyy-MM-dd', // this is the way it's sent to the backend
  validity: true,
  isTextInput: false,
  labelAnnotation: '',
})
const emit = defineEmits<Emits>()

defineExpose({ initCalendar })

const { getCurrentLanguage } = useLanguage()

const { t } = useI18n()

const calendarValue = ref<Date | null>(null)
const inputId = useId()

const currentLanguage = computed(() => getCurrentLanguage())

const displayFormat = computed(() => {
  switch (currentLanguage.value) {
    case 'en-US':
      return 'MM/dd/yyyy'
    case 'es':
    case 'it':
    case 'pt':
    case 'fr':
    case 'en-GB':
    case 'de':
    default:
      return 'dd/MM/yyyy'
  }
})

const placeholderText = computed(() => {
  switch (currentLanguage.value) {
    case 'de':
      return 'TT/MM/JJJJ'
    case 'en-GB':
      return 'DD/MM/YYYY'
    case 'en-US':
      return 'MM/DD/YYYY'
    case 'es':
      // Día/Mes/Año
      return 'DD/MM/AAAA'
    case 'fr':
      return 'JJ/MM/AAAA'
    case 'it':
      // Giorno/Mese/Anno
      return 'GG/MM/AAAA'
    case 'pt':
      // Día/Mes/Ano
      return 'DD/MM/AAAA'
    default:
      return 'DD/MM/YYYY'
  }
})

/**
 * The label annotation is used to display additional information about the field next to the label text.
 * The "labelAnnotation" prop is only used if the "isOptionalField" prop is false, since that takes precedence over other annotations.
 */
const labelAnnotation = computed(() =>
  props.isOptionalField
    ? t('common.optional')
    : props.labelAnnotation
      ? props.labelAnnotation
      : '',
)

onMounted(() => {
  initCalendar()
})

function initCalendar() {
  calendarValue.value = parseParentValue()
}

/**
 * Shorthand to get the calendar value from the parent Value.
 *
 * @returns The parsed Date value or null, will throw if invalid.
 */
function parseParentValue(): Date | null {
  if (!props.modelValue) return null

  const parsedValue = parse(props.modelValue, props.outputFormat, new Date())
  return parsedValue
}

/**
 * Once the calendar emits a new date, we need to emit the well formated output date.
 * As well as setting the calendar equivalent.
 *
 * @param $event - The emitted date from the calendar component.
 */
function handleDateInput($event: Date | null): void {
  if (!$event) return

  // If the date is invalid, we need to reset the calendar value and emit null to the parent.
  try {
    const newDate = format($event, props.outputFormat)
    calendarValue.value = $event
    emit('update:modelValue', newDate)
  } catch (_) {
    calendarValue.value = null
    emit('update:modelValue', null)
  }
}

/**
 * Once the user clean the input, we need to reset the calendar value
 * and emit null to the parent.
 */
function handleClearInput() {
  calendarValue.value = null
  emit('update:modelValue', null)
}
</script>

<template>
  <div>
    <LabelInput
      :html-for="`dp-input-${inputId}`"
      :label="label"
      :label-annotation="labelAnnotation"
    />
    <!-- Because the date picker is teleported rendering it server side might cause problems -->
    <!-- Reffer to this sentry: https://grooverco.sentry.io/issues/5642679960/replays/?project=4507112451932160 -->
    <ClientOnly>
      <VueDatePicker
        :uid="inputId"
        :model-value="calendarValue"
        :class="{ isInvalid: !validity }"
        :format="displayFormat"
        :placeholder="placeholderText"
        auto-apply
        :enable-time-picker="false"
        :locale="currentLanguage"
        :text-input="isTextInput"
        position="left"
        teleport
        :offset="1"
        @cleared="handleClearInput"
        @update:model-value="handleDateInput"
      >
        <template #icon-calendar>
          <i class="fa-solid fa-calendar-days" />
        </template>
      </VueDatePicker>
    </ClientOnly>
    <VText
      v-if="hasSlotContent($slots['default']) && !validity"
      html-tag="p"
      cfg="sans/14/medium"
      color="error"
      class="tw-mt-2"
    >
      <!--slot for error message (since this is the only slot in the file it can't be a named slot)-->
      <slot></slot>
    </VText>
  </div>
</template>

<!-- Purposefully don't scope these styles since calendar element attaches to the "body" when using the teleport option -->
<style lang="scss">
/**
* Override default 'vue-datepicker' themes & styles
* See https://vue3datepicker.com/customization/theming/#css-variables for more info
*/
:root {
  --dp-input-padding: 15px 40px 14px 16px;
  --dp-border-radius: 0.125rem;
  --dp-cell-size: 32px;
  --dp-cell-padding: 0;
}

.dp__input {
  @apply tw-h-12 tw-font-sans tw-leading-none tw-text-black;

  &:hover:not(:focus),
  &:hover:not(:focus-within) {
    @apply tw-border-gray-300;
  }

  &:focus,
  &:focus-within {
    @apply tw-border-black;
  }
}

.isInvalid {
  .dp__input {
    @apply tw-border-red-500;

    &:hover:not(:focus),
    &:hover:not(:focus-within) {
      @apply tw-border-red-500;
    }
  }
}

.dp__instance_calendar {
  @apply tw-shadow-hover;
}

.dp__month_year_select {
  @apply tw-font-sans tw-font-medium;
}

.dp__calendar_header_item {
  @apply tw-flex tw-items-center tw-justify-center tw-font-medium;
}

.dp__calendar_item,
.dp__calendar_header_item {
  flex-grow: unset;
}

.dp__arrow_top,
.dp__arrow_bottom,
.dp__calendar_header_separator {
  @apply tw-hidden;
}
</style>
